import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogOut, Home, Calendar, FileText, MapPin, History as HistoryIcon, Receipt, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function HistoryPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchHistory();
  }, [selectedMonth, viewMode]);

  const fetchHistory = async () => {
    try {
      const params = viewMode === 'month' 
        ? `month=${selectedMonth}` 
        : 'days=7';
      
      const response = await axios.get(`${BACKEND_URL}/api/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryData(viewMode === 'month' ? response.data : response.data.reverse());
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    }
  };

  const handleDateClick = (date) => {
    navigate(`/?date=${date}`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  // Generate calendar grid for the selected month
  const generateCalendar = () => {
    const [year, month] = selectedMonth.split('-');
    const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1);
    const lastDay = new Date(parseInt(year), parseInt(month), 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const calendar = [];
    let currentWeek = new Array(7).fill(null);
    
    // Fill in the days
    for (let day = 1; day <= daysInMonth; day++) {
      const dayIndex = (startDayOfWeek + day - 1) % 7;
      const dateStr = `${year}-${month}-${String(day).padStart(2, '0')}`;
      const dayData = historyData.find(d => d.date === dateStr);
      
      currentWeek[dayIndex] = {
        day,
        date: dateStr,
        data: dayData
      };
      
      if (dayIndex === 6 || day === daysInMonth) {
        calendar.push([...currentWeek]);
        currentWeek = new Array(7).fill(null);
      }
    }
    
    return calendar;
  };

  const calendar = viewMode === 'month' ? generateCalendar() : [];

  return (
    <div className="min-h-screen bg-black" data-testid="history-page">
      {/* Header compact */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700 h-14 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="home-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Caisse
          </Button>
          <Button
            onClick={() => navigate('/invoices')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <FileText className="w-4 h-4 mr-2" />
            Facturation
          </Button>
          <Button
            onClick={() => navigate('/deposit-receipts')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Bons d'entrée
          </Button>
          <Button
            onClick={() => navigate('/order-alerts')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Package className="w-4 h-4 mr-2" />
            Commandes
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-emerald-400 bg-white/10"
          >
            <HistoryIcon className="w-4 h-4 mr-2" />
            Historique
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/70">Bonjour, {user?.name}</span>
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      {/* Main Content - pleine largeur */}
      <main className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              Historique {viewMode === 'month' ? 'mensuel' : 'hebdomadaire'}
            </h2>
            <div className="flex gap-2 items-center">
              {viewMode === 'month' && (
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-auto bg-gray-800 border-gray-600 text-white [color-scheme:dark]"
                  data-testid="month-picker"
                />
              )}
              <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode(viewMode === 'month' ? 'week' : 'month')}
                  className="border-gray-600 text-white hover:bg-gray-800"
                  data-testid="toggle-view-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {viewMode === 'month' ? 'Vue 7 jours' : 'Vue mensuelle'}
                </Button>
              </div>
            </div>
          </div>

          {viewMode === 'month' && calendar.length > 0 && (
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6" data-testid="calendar-view">
              <h3 className="text-lg font-semibold text-white mb-4">Calendrier du mois</h3>
              <div className="grid grid-cols-7 gap-2">
                {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                  <div key={day} className="text-center font-semibold text-white/60 text-sm py-2">
                    {day}
                  </div>
                ))}
                {calendar.flat().map((cell, idx) => (
                  <div
                    key={idx}
                    onClick={() => cell && cell.data && handleDateClick(cell.date)}
                    className={`
                      min-h-[80px] p-2 border rounded-lg transition-colors
                      ${!cell ? 'bg-gray-800 border-gray-700' : cell.data ? 'bg-emerald-500/20 border-emerald-500/30 cursor-pointer hover:bg-emerald-500/30' : 'bg-gray-800 border-gray-700'}
                      ${cell && cell.data && cell.data.is_closed ? 'border-red-400/50' : ''}
                    `}
                    data-testid={cell ? `calendar-day-${cell.day}` : 'calendar-empty'}
                  >
                    {cell && (
                      <>
                        <div className="text-sm font-semibold text-white">
                          {cell.day}
                          {cell.data && cell.data.is_closed && <span className="ml-1 text-red-400">🔒</span>}
                        </div>
                        {cell.data && (
                          <div className="mt-1 text-xs">
                            <div className="font-mono text-emerald-300 font-semibold">{formatCurrency(cell.data.total_revenue)}</div>
                            <div className="text-white/50">{cell.data.count} ventes</div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {historyData.length > 0 ? (
            <>
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-6" data-testid="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={historyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke="rgba(255,255,255,0.6)" style={{ fontSize: '12px' }} />
                    <YAxis stroke="rgba(255,255,255,0.6)" style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#fff'
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px', color: '#fff' }} />
                    <Bar dataKey="total_cash" fill="#10B981" name="Espèces" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total_card" fill="#0EA5E9" name="Carte Bancaire" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden" data-testid="history-table">
                <table className="w-full text-base text-left">
                  <thead className="bg-gray-800 border-b border-gray-700 text-white/60 uppercase text-sm font-semibold">
                    <tr>
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4 text-right">Total</th>
                      <th className="px-5 py-4 text-right">Espèces</th>
                      <th className="px-5 py-4 text-right">Carte</th>
                      <th className="px-5 py-4 text-center">Transactions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {historyData.map((day, idx) => (
                      <tr 
                        key={idx} 
                        onClick={() => handleDateClick(day.date)}
                        className="hover:bg-gray-800 transition-colors cursor-pointer"
                        data-testid={`history-row-${day.date}`}
                      >
                        <td className="px-5 py-4 font-medium text-white">
                          <span className="flex items-center gap-2">
                            {day.date}
                            {day.is_closed && <span className="text-red-400 text-sm">🔒</span>}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right font-mono font-bold text-white text-lg">
                          {formatCurrency(day.total_revenue)}
                        </td>
                        <td className="px-5 py-4 text-right font-mono text-emerald-300 font-semibold">
                          {formatCurrency(day.total_cash)}
                        </td>
                        <td className="px-5 py-4 text-right font-mono text-sky-300 font-semibold">
                          {formatCurrency(day.total_card)}
                        </td>
                        <td className="px-5 py-4 text-center text-white/70">{day.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-12 text-center">
              <p className="text-white/40">Aucune donnée disponible</p>
            </div>
          )}
      </main>
    </div>
  );
}